import { useEffect, useRef, type JSX } from "react";

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      readonly sitekey: string;
      readonly callback: (token: string) => void;
      readonly "error-callback"?: () => void;
      readonly theme?: "auto" | "light" | "dark";
    },
  ) => string;
  remove: (widgetId: string) => void;
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** How long to wait for `window.turnstile` to appear before giving up polling. */
const API_READY_TIMEOUT_MS = 10_000;
const API_READY_POLL_MS = 50;

function turnstileApi(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

/** Normalizes the configured site key, ignoring unset and `<placeholder>` values. */
function resolveTurnstileSiteKey(value: string | undefined): string | undefined {
  return value !== undefined && value.trim().length > 0 && !value.trim().startsWith("<")
    ? value.trim()
    : undefined;
}

/**
 * Cloudflare Turnstile widget for abuse-resistant signup. When
 * `VITE_TURNSTILE_SITE_KEY` is not configured the widget is skipped and an empty
 * token is emitted, so local development does not require the service.
 *
 * The widget waits for `window.turnstile` to become available before rendering.
 * This matters under React StrictMode (and on remount) where the `<script>` is
 * already in the DOM but has not executed yet: rendering synchronously would be a
 * silent no-op, leaving the form without a token.
 */
export function TurnstileWidget({
  onToken,
}: {
  readonly onToken: (token: string) => void;
}): JSX.Element {
  const siteKey = resolveTurnstileSiteKey(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const containerRef = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (siteKey === undefined) {
      onTokenRef.current("");
      return;
    }

    let cancelled = false;
    let widgetId: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + API_READY_TIMEOUT_MS;

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing === null) {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const render = (api: TurnstileApi): void => {
      if (containerRef.current === null) return;
      widgetId = api.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onTokenRef.current(token),
        // Clear the token on failure so a stale value is never submitted.
        "error-callback": () => onTokenRef.current(""),
      });
    };

    const poll = (): void => {
      if (cancelled) return;
      const api = turnstileApi();
      if (api !== undefined) {
        render(api);
        return;
      }
      if (Date.now() < deadline) timer = setTimeout(poll, API_READY_POLL_MS);
    };
    poll();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      const api = turnstileApi();
      if (widgetId !== null && api !== undefined) api.remove(widgetId);
    };
  }, [siteKey]);

  if (siteKey === undefined) {
    return (
      <p className="text-muted-foreground text-xs">
        Turnstile is not configured; signup will be submitted without a challenge token.
      </p>
    );
  }

  return <div ref={containerRef} />;
}
